'use client';

import classNames from 'classnames';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { isProduction } from '@alltrails/core/utils/Environment';
import Accordion from '@alltrails/denali/components/Accordion';
import Button from '@alltrails/denali/components/Button';
import Checkbox from '@alltrails/denali/components/Checkbox';
import IconButton from '@alltrails/denali/components/IconButton';
import LoadingDots from '@alltrails/denali/components/LoadingDots';
import TextInput from '@alltrails/denali/components/TextInput';
import Close from '@alltrails/denali/icons/Close';
import Experiments from '@alltrails/types/Experiments';
import styles from './styles.module.scss';

type Props = {
  userExps: Experiments;
  refresh?: boolean;
};

interface URLParams {
  [key: string]: any;
}

// Add cross-platform experiment keys here when needed.
const CROSS_PLATFORM_EXPERIMENTS: (keyof Experiments)[] = [];

const Flags = ({ userExps }: Props) => {
  const [experiments, setExperiments] = useState<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const classes = classNames(styles.flagContainer, isOpen ? styles.active : '');
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const isProd = isProduction();

  const [search, setSearch] = useState<string>('');

  const handleRoute = (newParams: URLParams) => {
    const params = new URLSearchParams(searchParams);

    for (const [key, value] of Object.entries(newParams)) {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    }

    window.location.assign(`${pathname}?${params.toString()}`);
  };

  useEffect(() => {
    if (isProd) {
      return;
    }
    async function getAmplitudeList() {
      const amplitudeData = await fetch('/api/experiments/getlist').then(data => data.json());
      const experimentsData = amplitudeData?.experiments ?? [];
      const flagsData = amplitudeData?.flags ?? [];
      const results = [...experimentsData, ...flagsData].filter(
        item => item?.key?.split('-')[0] === 'web' || CROSS_PLATFORM_EXPERIMENTS.includes(item?.key)
      );
      const userResults = results?.map(item => Object.assign(item, userExps[item.key] ? { value: userExps[item.key] } : null));
      const sortedUserResults = userResults?.sort((a, b) => a.key.localeCompare(b.key));
      setExperiments(sortedUserResults);
    }

    getAmplitudeList();
  }, [isProd, userExps]);

  if (isProd) {
    return;
  }

  const hideButton = searchParams.get('hideFlagsCta') === 'true';

  const filteredExperiments = search
    ? experiments?.filter(item => item.key.toLowerCase().includes(search.toLowerCase()) || item.name.toLowerCase().includes(search.toLowerCase()))
    : experiments;

  return (
    <>
      {!hideButton && (
        <Button text="Experiments" className={styles.flagController} aria-label="Experiments" onClick={() => setOpen(!isOpen)} variant="primary" />
      )}
      <div className={classes}>
        <h2 className={styles.title}>Experiments</h2>
        <IconButton
          className={styles.closeButton}
          icon={{ Component: Close }}
          onClick={() => setOpen(false)}
          size="sm"
          title="Close"
          variant="ghost"
          elevated
        />
        <div className={styles.buttonGroup}>
          <Button
            text="Reset"
            aria-label="Experiments"
            onClick={() => handleRoute({ expId: null, variantId: null, controlExps: null, resetExps: true })}
            variant="primary"
            width="full"
          />
          <Button
            text="Control"
            aria-label="Experiments"
            onClick={() => handleRoute({ resetExps: null, expId: null, variantId: null, controlExps: true })}
            variant="accent"
            width="full"
          />
        </div>
        <TextInput className={styles.searchInput} placeholder="Search by key or name" value={search} onChange={setSearch} size="sm" />
        {filteredExperiments ? (
          filteredExperiments.map(item => (
            <Accordion title={item.name} key={item.id} variant="minimal" initiallyOpen={false} headingTypography="text100bold">
              <div className={styles.subText}>
                Experiment Status: {item.enabled ? <span className={styles.green}>Enabled</span> : <span className={styles.red}>Disabled</span>}
              </div>
              <div className={styles.variantContainer}>
                {item.variants.map(variant => (
                  <div key={variant.key} className={styles.item}>
                    <Checkbox
                      labelElement={variant.name || variant.key}
                      id={variant.id}
                      key={variant.id}
                      selected={(item.value && variant?.key === item?.value?.key) || (item?.value?.key === 'off' && variant.key === 'control')}
                      onChange={e =>
                        e
                          ? handleRoute({ resetExps: null, expId: item.key, variantId: variant.key })
                          : handleRoute({ resetExps: null, expId: item.key, variantId: 'control' })
                      }
                      size="sm"
                    />
                  </div>
                ))}
              </div>
            </Accordion>
          ))
        ) : (
          <LoadingDots className={styles.loading} />
        )}
      </div>
    </>
  );
};

export default Flags;
